import React, { useState } from "react";
import { FolderKanban, Loader, Link2 } from "lucide-react";
import { ProjectSelector } from "./ProjectSelector";

interface ProjectBadgeProps {
  projectId: string | null | undefined;
  projectName?: string | null;
  onChange: (projectId: string | null) => Promise<void>;
  disabled?: boolean;
}

export const ProjectBadge: React.FC<ProjectBadgeProps> = ({
  projectId,
  projectName,
  onChange,
  disabled = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId || null);

  const handleConfirm = async () => {
    if (disabled || isUpdating) return;
    
    setIsUpdating(true);
    try {
      await onChange(selectedProjectId);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update project:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenModal = () => {
    setSelectedProjectId(projectId || null);
    setIsModalOpen(true);
  };

  if (isUpdating) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
        <Loader className="w-3 h-3 animate-spin" />
        <span className="text-xs font-medium">Updating...</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => !disabled && handleOpenModal()}
        disabled={disabled}
        className={`
          inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold
          transition-all duration-150 ease-in-out
          ${!projectId 
            ? 'bg-gray-100 dark:bg-[rgba(89,102,121,0.5)] text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-[rgba(89,102,121,0.6)]' 
            : 'bg-accent-100 dark:bg-accent-800 text-accent-700 dark:text-accent-300 hover:bg-accent-200 dark:hover:bg-accent-700'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        <FolderKanban className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="max-w-[140px] truncate">
          {projectId ? (projectName || "Project") : "Not mapped"}
        </span>
      </button>

      {/* Map to Project Modal - Same style as BulkActionBar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Map to Project
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select a project to map this bill to. This will link them for profit & loss reporting.
            </p>
            <div className="mb-6">
              <ProjectSelector
                value={selectedProjectId}
                onChange={setSelectedProjectId}
                placeholder="None (Unmapped)"
                disabled={isUpdating}
                dropdownZIndex={10002}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedProjectId(projectId || null);
                }}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center gap-2"
              >
                {isUpdating ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Mapping...
                  </>
                ) : (
                  "Map to Project"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
